/**
 * The Today view model (13-09): which rows are cards, which sit under "In others' hands", and what
 * is "Done today". Pure, so the lens rules are proven without React.
 *
 *   - The queue = `lensFor(actor).myQueue` + `handedBack` (Oversight, ISS-186) + `myVerifications`
 *     (Oversight; verified or rejected from the card, 13-10). It is sorted by `compareQueue`.
 *   - A verification card always says why it can't be acted on yet when `canDoVerb` refuses it
 *     (ISS-188: `completedByUpn` comes from `loadVerifyContext`). It is never silently hidden. A
 *     row I completed moves to "In others' hands": nobody verifies their own work.
 *   - "Done today" = this session's committed actions plus today's (Melbourne) completed rows of
 *     mine. Reopen is offered on those still `Done`.
 *   - ISS-212: Oversight's released, still-open cards carry `retire` (a quiet "Retire…" with a
 *     required note of at least 10 characters). A session retire shows in Done today as "Retired".
 *
 * `applyPatch` replays a verb's patch onto a row, so a committed action shows at once (practice
 * mode never refetches a changed row).
 */

import { AR_FIELDS, AR_LOOKUP_FIELDS } from '../config/actionRegister';
import { canDoVerb, isRegulatoryDecision, lensFor } from './actionAuthority';
import type { AuthorityReason } from './actionAuthority';
import { compareQueue } from './briefing';
import { melbourneDay } from './actionVerbs';
import type { ActedAt } from './actionVerbs';
import type { ActionRow, ActionVerb, Actor, OwnerRole } from '../types/actionRegister';

// ---------------------------------------------------------------------------
// applyPatch
// ---------------------------------------------------------------------------

const F = AR_FIELDS;
const L = AR_LOOKUP_FIELDS;

/** Patch key (SP internal name) → the `ActionRow` field it sets. */
const PATCH_TO_ROW: Record<string, keyof ActionRow> = {
  [F.status]: 'status',
  [F.acceptedAt]: 'acceptedAt',
  [F.completedAt]: 'completedAt',
  [F.verifiedAt]: 'verifiedAt',
  [F.releasedAt]: 'releasedAt',
  [F.releasedBy]: 'releasedBy',
  [F.releaseNote]: 'releaseNote',
  [F.evidence]: 'evidence',
  [F.verdict]: 'verdict',
  [F.escalation]: 'escalation',
  [F.decidedOption]: 'decidedOption',
  [F.ownerRole]: 'ownerRole',
  [L.owner]: 'ownerLookupId',
  [L.verifiedBy]: 'verifiedByLookupId',
};

/** The row as it reads once `patch` has landed. Unknown keys are ignored. */
export function applyPatch(row: ActionRow, patch: Record<string, unknown>, eTag?: string): ActionRow {
  const next: Record<string, unknown> = { ...row };
  for (const [key, value] of Object.entries(patch)) {
    const field = PATCH_TO_ROW[key];
    if (field) next[field] = value === undefined || value === '' ? null : value;
  }
  if (Object.prototype.hasOwnProperty.call(patch, L.owner) && patch[L.owner] == null) next.owner = null;
  if (eTag) next.eTag = eTag;
  return next as unknown as ActionRow;
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export type CardKind = 'action' | 'evidence' | 'decision' | 'regulatory' | 'verify' | 'handedBack';

export function cardKind(row: ActionRow): CardKind {
  if (row.status === 'NotMine') return 'handedBack';
  if (row.status === 'Done') return 'verify';
  if (row.kind === 'Decision') return isRegulatoryDecision(row).regulatory ? 'regulatory' : 'decision';
  if (row.verificationMethod === 'evidence' && row.status !== 'Proposed') return 'evidence';
  return 'action';
}

/** Who completed a row (ISS-188), as the page has it so far. */
export type CompleterState =
  | { status: 'loading' }
  | { status: 'ready'; completedByUpn?: string }
  | { status: 'error' };

/** Why a verification card can't be acted on: an authority code, or the completer read state. */
export type CardRefusal = AuthorityReason | 'checking' | 'unreadable';

export interface QueueCard {
  row: ActionRow;
  kind: CardKind;
  /** Verification cards only: set when Verify would be refused, so the card can say why. */
  refusal?: CardRefusal;
  /** Verification cards only: who completed the row (ISS-188), once read. */
  completedByUpn?: string;
  /**
   * ISS-212: Oversight may retire this released, still-open row from the card, a quiet secondary
   * action ("Retire…") with a required note of at least `RETIRE_NOTE_MIN` characters. Never set on
   * a verification (Done) or handed-back (NotMine) card, nor for anyone but Oversight.
   */
  retire?: true;
}

/** ISS-212: the shortest retire note the card accepts, in characters after trimming. */
export const RETIRE_NOTE_MIN = 10;

/** Is this retire note long enough to send (`RETIRE_NOTE_MIN`, surrounding space ignored)? */
export function retireNoteReady(note: string): boolean {
  return note.trim().length >= RETIRE_NOTE_MIN;
}

export interface OtherItem {
  id: number;
  title: string;
  text: string;
  /** A regulatory call Oversight makes: the row, so "View context" can open it read-only. */
  context?: ActionRow;
}

/** One action this session committed. `row` is the row as it reads afterwards. */
export interface SessionAction {
  actionId: string;
  id: number;
  verb: ActionVerb;
  title: string;
  /** ISO: when the person acted. */
  at: string;
  /** Who acted (the outbox entry's owner): the completer of a session `done`. */
  byUpn: string;
  row: ActionRow;
}

export interface DoneItem {
  id: number;
  title: string;
  /** "Done", "Reassigned". */
  verb: string;
  /** ISO, or null when unknown. */
  at: string | null;
  /** The row as it reads now: Reopen is offered while it is `Done`. */
  row: ActionRow;
  /** Set when this session did it: its completer is then known without a read. */
  sessionBy?: string;
}

export interface TodayView {
  queue: QueueCard[];
  others: OtherItem[];
  done: DoneItem[];
}

function sameUpn(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

const DONE_VERB_LABEL: Partial<Record<ActionVerb, string>> = {
  done: 'Done',
  reassign: 'Reassigned',
  decide: 'Decided',
  verify: 'Verified',
  reject: 'Returned',
  retire: 'Retired',
};

export interface BuildTodayInput {
  rows: readonly ActionRow[];
  actor: Actor;
  now: ActedAt;
  completers?: Readonly<Record<number, CompleterState>>;
  session?: readonly SessionAction[];
  /** Cards on their way out (or held while saving): left out of the queue. */
  hidden?: ReadonlySet<number>;
}

export function buildToday(input: BuildTodayInput): TodayView {
  const { rows, actor, now } = input;
  const completers = input.completers ?? {};
  const session = input.session ?? [];
  const hidden = input.hidden ?? new Set<number>();
  const lens = lensFor(actor);
  const oversight = actor.roles.includes('Oversight');
  const today = melbourneDay(now);

  const queueRows: ActionRow[] = [];
  const refusal = new Map<number, CardRefusal>();
  const completedBy = new Map<number, string>();
  const others: OtherItem[] = [];

  for (const row of rows) {
    if (lens.myQueue(row) || lens.handedBack(row)) {
      queueRows.push(row);
      continue;
    }
    if (lens.myVerifications(row)) {
      const c = completers[row.id];
      if (c?.status === 'ready' && sameUpn(c.completedByUpn, actor.upn)) {
        others.push({
          id: row.id,
          title: `You finished ${row.reference}: ${row.title}`,
          text: 'Another verifier checks it. Nobody verifies their own work.',
        });
        continue;
      }
      queueRows.push(row);
      if (!c || c.status === 'loading') refusal.set(row.id, 'checking');
      else if (c.status === 'error') refusal.set(row.id, 'unreadable');
      else {
        if (c.completedByUpn) completedBy.set(row.id, c.completedByUpn);
        const ok = canDoVerb('verify', row, actor, { completedByUpn: c.completedByUpn });
        if (!ok.ok) refusal.set(row.id, ok.reason);
      }
      continue;
    }
    // A regulatory call on a row of mine: Oversight decides it, and I can see it's in hand.
    if (
      !oversight &&
      row.kind === 'Decision' &&
      isRegulatoryDecision(row).regulatory &&
      !!row.releasedAt &&
      ['Proposed', 'Accepted', 'InProgress'].includes(row.status) &&
      canDoVerb('accept', row, actor).ok
    ) {
      others.push({ id: row.id, title: `${row.reference}: ${row.title}`, text: 'Oversight makes this call.', context: row });
    }
  }

  // Handed back this session (Not mine): with Oversight now, not in Done today (D-13-02-1).
  const byId = new Map(rows.map((r) => [r.id, r]));
  const handedBackNow = new Set<number>();
  for (const s of session) {
    if (s.verb !== 'notMine') continue;
    const current = byId.get(s.id);
    if (current && current.status !== 'NotMine') continue;
    if (handedBackNow.has(s.id)) continue;
    handedBackNow.add(s.id);
    others.unshift({ id: s.id, title: s.title, text: 'Handed back to Oversight to reassign' });
  }

  // Done today: this session first (newest first), then today's completions of mine.
  const done: DoneItem[] = [];
  const seen = new Set<number>();
  for (const s of [...session].reverse()) {
    const label = DONE_VERB_LABEL[s.verb];
    if (!label || seen.has(s.id)) continue;
    const current = byId.get(s.id) ?? s.row;
    if (s.verb === 'done' && current.status !== 'Done' && current.status !== 'Verified') continue; // reopened
    seen.add(s.id);
    done.push({ id: s.id, title: s.title, verb: label, at: s.at, row: current, sessionBy: s.byUpn });
  }
  for (const row of rows) {
    if (seen.has(row.id) || row.status !== 'Done' || !row.completedAt) continue;
    if (melbourneDay(row.completedAt) !== today) continue;
    const c = completers[row.id];
    const mine =
      (c?.status === 'ready' && sameUpn(c.completedByUpn, actor.upn)) ||
      (!!actor.lookupId && row.ownerLookupId === actor.lookupId);
    if (!mine) continue;
    seen.add(row.id);
    done.push({ id: row.id, title: row.title, verb: 'Done', at: row.completedAt, row });
  }

  // A row of mine finished today is already under Done today: don't say it twice.
  const doneIds = new Set(done.map((d) => d.id));
  const othersShown = others.filter((o) => !(doneIds.has(o.id) && o.text.startsWith('Another verifier')));

  const queue = queueRows
    .filter((r) => !hidden.has(r.id))
    .sort(compareQueue(now))
    .map((row) => {
      const card: QueueCard = { row, kind: cardKind(row) };
      const why = refusal.get(row.id);
      if (why) card.refusal = why;
      const by = completedBy.get(row.id);
      if (by) card.completedByUpn = by;
      // ISS-212: released and open (the authority asks Oversight + open; the release is asked here,
      // because an unreleased row is retired in the sitting, not from a card).
      if (!!row.releasedAt?.trim() && canDoVerb('retire', row, actor).ok) card.retire = true;
      return card;
    });

  return { queue, others: othersShown, done };
}

/** Plain words for a card refusal (ISS-188: said, never hidden). */
export function cardRefusalText(reason: CardRefusal | string): string {
  switch (reason) {
    case 'completer-unknown':
      return "Who completed this can't be confirmed, so it can't be verified yet.";
    case 'no-self-verification':
      return 'You completed this, so someone else verifies it.';
    case 'not-completer':
      return 'Only the person who completed this can reopen it.';
    case 'not-released':
      return "This hasn't been released yet.";
    case 'oversight-only':
      return 'Only Oversight can do this.';
    case 'not-owner':
      return "This isn't yours to act on.";
    case 'regulatory-oversight-only':
      return 'Oversight makes this call.';
    case 'reopen-window-closed':
      return 'This was completed on an earlier day, so it can only be reopened on the day.';
    case 'not-open':
      return "This is no longer open, so it can't be retired.";
    case 'checking':
      return 'Checking who completed this…';
    case 'unreadable':
      return "Who completed this couldn't be read. Reload to try again.";
    default:
      return "This can't be changed here.";
  }
}

// ---------------------------------------------------------------------------
// Card words
// ---------------------------------------------------------------------------

export const ROLE_LABEL: Record<OwnerRole, string> = {
  Oversight: 'Oversight',
  CareManager: 'Care Manager',
  HRWHS: 'HR & WHS',
  Finance: 'Finance',
  Admin: 'Admin',
  Board: 'Board',
  QCAC: 'QCAC',
  Wellness: 'Wellness',
  DAG: 'DAG',
};

/** A SharePoint sharing link longer than the 255 a short ref fits in. */
export function isLongSharingLink(text: string): boolean {
  const t = text.trim();
  return t.length > 255 && /^https?:\/\//i.test(t) && /sharepoint\.com/i.test(t);
}

/** Plain words for a refused tap (an authority code or a 13-04 VerbError code). */
export function runRefusalText(reason: string): string {
  switch (reason) {
    case 'evidence-required':
      return 'Done needs the proof: a ref or a short pointer.';
    case 'note-required':
      return 'Add a note for the new owner first.';
    case 'owner-role-required':
      return 'Choose who should own it now.';
    case 'already-accepted':
      return 'This is already accepted.';
    case 'invalid-transition':
    case 'terminal':
      return 'This has changed since it loaded. Reload to see where it is now.';
    case 'busy':
      return 'Still saving the last change to this one.';
    case 'no-change':
      return 'Nothing to change.';
    case 'option-required':
      return 'Choose an option first.';
    case 'reason-required':
      return 'Add a reason first.';
    case 'option-too-long':
      return 'Keep the decision to 255 characters. Put the detail in the reason.';
    case 'not-a-decision':
    case 'not-verifiable':
      return "This can't be decided or verified from here.";
    case 'invalid-verdict':
      return 'Sampled OK only applies to a sampled row.';
    default:
      return cardRefusalText(reason);
  }
}

// ---------------------------------------------------------------------------
// Why a Decision is treated as regulatory (D-13-00-2), in words
// ---------------------------------------------------------------------------

/**
 * The card's small "Treated as regulatory: …" line, from `isRegulatoryDecision(row).because`.
 * Always shown on a regulatory card, so a wrong classification is visible and can be fixed (by
 * setting `DecisionClass` on the row). Mirrors the classifier's own codes.
 */
export function regulatoryBecauseText(because: string): string {
  if (because === 'classified') return 'Treated as regulatory: classified on the row';
  if (because === 'ParentType=Incident' || because === 'Source=Incident') return 'Treated as regulatory: from an incident';
  const type = /^ActionType=(.+)$/.exec(because);
  if (type) return `Treated as regulatory: its type is ${type[1]}`;
  const match = /^(Title|Options|ActionType)~\/(.*)\/$/.exec(because);
  if (match) {
    const where = match[1] === 'Title' ? 'its title' : match[1] === 'Options' ? 'its options' : 'its type';
    return `Treated as regulatory: ${where} mention${match[1] === 'Options' ? '' : 's'} “${match[2]}”`;
  }
  return 'Treated as regulatory';
}
